/**
 * Getting a photograph ready to store: right way up, small enough, and dated.
 *
 * A phone photograph arrives several megabytes large, possibly sideways, and
 * with the day it was taken buried in its EXIF. All three are fixed here,
 * before anything is uploaded, so every route into Grow — the camera, the
 * file picker, Google Photos, the profile page — gets the same treatment.
 *
 * An iPhone's HEIC is turned into a JPEG first (shared/lib/uploadablePhoto),
 * since the server cannot read one. Its date is still read from the original:
 * the JPEG the converter makes carries no EXIF.
 *
 * Every photo longer than 2048 pixels on a side is shrunk to fit inside 2048
 * (B82; lib/photoShrink), as a photo over 5 MB is: the live server stopped in
 * the middle of a full-size upload, which it decodes several times. A
 * 12-megapixel phone photo (4000 by 3000) becomes 2048 by 1536 and well under
 * 1 MB; a 48-megapixel one (8064 by 6048) the same. The server's own pixel
 * limit (lib/photoLimit, the Grow setting photos.MAX_EDGE_PX) still applies
 * when it is the smaller. A photo already inside the box and light enough is
 * sent as it is. A file the browser cannot decode is left for the server to
 * judge. Any other failure is a refusal: preparePhoto does not send such a
 * picture, and says error.message.
 *
 * It used to also upload, delete and edit photos, in two hundred and fifty
 * lines that duplicated shared/api/photos.ts and that nothing had called in a
 * long time. A class that both prepares a file and talks to the server is two
 * classes; this is the half that was being used.
 *
 * No network: it reads and redraws the file in the browser. Who uses it:
 * every photo upload (the plant photo dialogs and usePhotoChoice, the bed's
 * cover, the tray's uploader, the seed-packet scan, Today's own picture,
 * the profile photo), all through shared/lib/preparePhoto, which holds the one
 * PhotoProcessor and the one handling of a picture that cannot be made ready.
 *
 * @module PhotoProcessor
 */

import exifr from 'exifr';
import { debugLog, APP_CONSTANTS } from '@/config/config';
import { growLimit } from '@/shared/lib/growSettings';
import { parseDate, toISOString } from '@/shared/utils/dateUtils';
import { DateTime } from '@/shared/utils/dateUtils';
import { t } from '@/shared/i18n/i18nSetup';
import { toUploadable } from '@/shared/lib/uploadablePhoto';
import { longestEdge, photoMaxEdge } from '@/shared/lib/photoLimit';
import { needsShrink, shrinkSize } from '@/shared/lib/photoShrink';

/** Success with the result, or failure saying at which step and why. Never thrown. */
interface PhotoProcessResult<T> {
  /** Whether the photo is ready. */
  success: boolean;
  /** The result, on success. */
  data?: T;
  /** What went wrong, on failure: message is a sentence for the gardener; details are for the log. */
  error?: {
    /**
     * Which step failed. 'convert' is an iPhone photo that could not be made a
     * JPEG: sending the original instead would only be refused.
     */
    stage: 'convert' | 'resize' | 'exif' | 'upload' | 'ai';
    message: string;
    details: {
      status?: number;
      url?: string;
      message?: string;
      note?: string;
      error?: unknown;
    };
  };
}

/** A photograph ready to upload, and what was learnt from it on the way. */
interface ProcessedPhoto {
  /** The file to upload: the original, or a JPEG of it (converted from HEIC, shrunk, or both). */
  file: File;
  /** What the photo's EXIF said, read from the file as chosen. */
  exifData?: {
    /** The day, floored: what a photo's date field is set from. */
    dateTaken?: DateTime;
    /** The moment, to the second, for telling one pile of photos from the next. */
    takenAt?: DateTime;
    /** Not filled today; the browser turns a photo upright as it decodes it. */
    orientation?: number;
  };
  /** The shrunk picture's size, in pixels; absent when it was not shrunk. */
  dimensions?: {
    width: number;
    height: number;
  };
  /** Not filled today (file holds the shrunk picture). */
  resized?: Blob;
}

/**
 * The canvas as a JPEG.
 *
 * toBlob hands back null when the browser cannot encode the canvas (one too
 * large for its memory, say). That used to be passed on as though it were a
 * picture and fail a line later reading its size; it is an error, said here.
 *
 * @param canvas - The drawn picture.
 * @param quality - JPEG quality, 0 to 1.
 * @returns The encoded picture.
 */
function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode the image'))),
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Prepares one photograph at a time: checks its type, reads its date, and
 * shrinks it when it is too large.
 */
export class PhotoProcessor {
  /**
   * The largest file uploaded as it is, in bytes: the server's limits.PHOTO_MAX_MB mebibytes
   * (no limit before the app has heard it, when the server's refusal stands).
   * BYTES_PER_MB is 1024 (really bytes per kibibyte), so it is used twice.
   */
  private get maxSize(): number {
    const megabytes = growLimit('PHOTO_MAX_MB');
    return megabytes ? megabytes * APP_CONSTANTS.PHOTO.BYTES_PER_MB * APP_CONSTANTS.PHOTO.BYTES_PER_MB : Infinity;
  }
  /** The widest a shrunk photo is, in pixels. */
  private maxWidth = APP_CONSTANTS.PHOTO.MAX_WIDTH;
  /** The tallest a shrunk photo is, in pixels. */
  private maxHeight = APP_CONSTANTS.PHOTO.MAX_HEIGHT;

  /**
   * Whether the file is a type Grow stores (the server's limits.PHOTO_TYPES; any type before the app has heard them, when the server's refusal stands).
   *
   * @param file - The photo.
   * @returns True for an allowed type.
   */
  private validateFileType(file: File): boolean {
    const types = growLimit('PHOTO_TYPES');
    return types ? types.includes(file.type) : true;
  }

  /**
   * Gets a photograph ready: an iPhone HEIC made a JPEG, its type checked,
   * its EXIF date read from the file as chosen (before anything can strip
   * it), shrunk when larger than maxSize or longer on a side than the shrink
   * box (2048 pixels, B82) or the server's pixel limit (photos.MAX_EDGE_PX),
   * whichever is smaller.
   *
   * @param file - The photograph as chosen.
   * @returns The file to upload and its date, or why it could not be prepared.
   *   A HEIC that could not be converted fails at the 'convert' stage, with a
   *   sentence for the gardener as its message.
   */
  async processPhoto(file: File): Promise<PhotoProcessResult<ProcessedPhoto>> {
    try {
      // A HEIC becomes a JPEG; anything else comes back as it was.
      let uploadable: File;
      try {
        uploadable = await toUploadable(file);
      } catch (error) {
        debugLog('PhotoProcessor', 'Could not convert a HEIC photo:', error);
        return {
          success: false,
          error: {
            stage: 'convert',
            message: error instanceof Error ? error.message : t('plants.photo.heicFailed'),
            details: { note: file.type || file.name, error },
          },
        };
      }

      // Said as itself, not lost in the catch below's general "something failed".
      if (!this.validateFileType(uploadable)) {
        return { success: false, error: { stage: 'upload', message: t('plants.photo.wrongType'), details: { note: file.type || 'no type' } } };
      }

      // From the original: a converted JPEG has no EXIF, and a resize drops it too.
      const exifResult = await this.processExif(file);
      debugLog('PhotoProcessor', 'EXIF processing result:', exifResult);

      let processedFile = uploadable;
      let dimensions;
      
      // Shrink a heavy file, or one longer on a side than the shrink box or the
      // server's limit (B82: every phone photo, not only the heaviest; a file
      // the browser cannot decode is left to the server to judge).
      const box = Math.max(this.maxWidth, this.maxHeight);
      const serverEdge = photoMaxEdge();
      const maxEdge = Math.min(box, serverEdge ?? box);
      const tooHeavy = uploadable.size > this.maxSize;
      const edge = tooHeavy ? null : await longestEdge(uploadable);
      if (needsShrink({ edge, bytes: uploadable.size, maxBytes: this.maxSize, maxEdge })) {
        const resizeResult = await this.resizeImage(uploadable, maxEdge);
        if (!resizeResult.success || !resizeResult.data) {
          return resizeResult;
        }
        processedFile = resizeResult.data.file;
        dimensions = resizeResult.data.dimensions;
      }

      return {
        success: true,
        data: {
          file: processedFile,
          exifData: exifResult.data,
          dimensions
        }
      };
    } catch (error) {
      debugLog('PhotoProcessor', 'Process photo error:', error);
      return {
        success: false,
        error: {
          stage: 'upload',
          message: t('plants.photo.processFailed'),
          details: error instanceof Error ? { message: error.message } : { error }
        }
      };
    }
  }

  /**
   * The moment the photograph was taken, from its EXIF: when it was taken,
   * else created, else modified. A file with no EXIF, or none readable, is
   * not a failure; it simply has no date.
   *
   * @param file - The photograph.
   * @returns The day (floored) and the moment, either possibly absent.
   */
  private async processExif(file: File): Promise<PhotoProcessResult<{ dateTaken?: DateTime; takenAt?: DateTime }>> {
    try {
      const exif = await exifr.parse(file);
      debugLog('PhotoProcessor', 'EXIF data:', exif);

      let takenAt: DateTime | undefined;

      // Try different date fields in order of preference
      const stamp = exif?.DateTimeOriginal ?? exif?.CreateDate ?? exif?.ModifyDate;
      if (stamp) {
        takenAt = parseDate(stamp.toISOString());
      }
      const dateTaken = takenAt?.startOf('day');

      if (dateTaken) {
        debugLog('PhotoProcessor', 'Found EXIF date:', toISOString(dateTaken));
      }

      return {
        success: true,
        data: { dateTaken, takenAt }
      };
    } catch (error) {
      debugLog('PhotoProcessor', 'EXIF processing warning:', error);
      return {
        success: true,
        data: { dateTaken: undefined }
      };
    }
  }

  /**
   * Draws the photograph smaller, inside maxWidth by maxHeight (and maxEdge,
   * when the server's limit is smaller), as a JPEG, stepping the quality down
   * until it fits under maxSize or reaches the lowest quality allowed.
   *
   * @param file - The photograph.
   * @param maxEdge - The longest side allowed, in pixels.
   * @returns The shrunk file and its size, or why it could not be shrunk.
   */
  private async resizeImage(file: File, maxEdge: number): Promise<PhotoProcessResult<{ file: File; dimensions: { width: number; height: number } }>> {
    try {
      // Create an image element to load the file
      const img = document.createElement('img');
      const reader = new FileReader();

      // Create a promise to handle the async operations
      const imageLoaded = new Promise<HTMLImageElement>((resolve, reject) => {
        reader.onload = (e) => {
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
      });

      // Read the file
      reader.readAsDataURL(file);
      
      // Wait for image to load
      const loadedImg = await imageLoaded;

      // Inside the box, its shape kept (lib/photoShrink).
      const { width, height } = shrinkSize(
        loadedImg.width,
        loadedImg.height,
        Math.min(this.maxWidth, maxEdge),
        Math.min(this.maxHeight, maxEdge),
      );

      // Create canvas and resize
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // On white first: a transparent PNG saved as JPEG would come out on black.
      ctx.fillStyle = APP_CONSTANTS.PHOTO.FLATTEN_BACKGROUND;
      ctx.fillRect(0, 0, width, height);

      // Draw image with smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = APP_CONSTANTS.PHOTO.SMOOTHING;
      ctx.drawImage(loadedImg, 0, 0, width, height);

      // Convert to blob with reduced quality if still too large
      const maxQuality = APP_CONSTANTS.PHOTO.QUALITY;
      let quality = maxQuality;
      let blob = await canvasToJpeg(canvas, quality);

      // Reduce quality if still too large
      while (blob.size > this.maxSize && quality > APP_CONSTANTS.PHOTO.MIN_QUALITY) {
        quality -= APP_CONSTANTS.PHOTO.QUALITY_REDUCTION_STEP;
        blob = await canvasToJpeg(canvas, quality);
      }

      // Create new file from blob
      const resizedFile = new File([blob], file.name, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });

      return {
        success: true,
        data: { 
          file: resizedFile,
          dimensions: { width, height }
        }
      };
    } catch (error) {
      debugLog('PhotoProcessor', 'Resize error:', error);
      return {
        success: false,
        error: {
          stage: 'resize',
          message: t('plants.photo.resizeFailed'),
          details: error instanceof Error ? { message: error.message } : { error }
        }
      };
    }
  }
}
